import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useFormik } from "formik";
import { useSelector } from "react-redux";

import {
  Button,
  CustomSelect,
  Error,
  Input,
  Label,
  SelectBadla,
  Span,
  Switch,
} from "../../Components";
import {
  badlaOrderSchema,
  instrumentInsfoSchema,
} from "../../Schema/BadlaSchema";
import {
  API_BADLA_LAST_PRICE,
  API_BADLA_ORDER_ADD,
  API_BADLA_ORDER_EDIT,
  API_UPDATE_SCRIPTS,
} from "../../utils/api/ApiConstant";
import {
  postAxios,
  postFetchAxios,
  putAxios,
} from "../../Services/commonService";
import { brokerageTypeOptions, orderTypeOptions } from "../../Static/data";
import { useBadlaInstrumentList } from "../../Hooks";
import {
  generateRandomId,
  handleEquationParams,
  isNum,
} from "../../helper/common";

export const MODAL_MODE = {
  ADD: "add",
  EDIT: "edit",
  END: "end",
  BUY: "buy",
  SELL: "sell",
};

const AddBadlaOrder = ({
  rowData = {},
  handleShowHide,
  badlaOrderRefetch = () => {},
}) => {
  const userId = useSelector((state) => state.user?.user?.userId);

  const findOption = (options, value) =>
    options.find((opt) => opt.value === value) ?? null;

  const isCreateFromBadla = !!rowData?.createFromBadla;
  const isEdit = !!rowData?.isEdit;

  const {
    values,
    errors,
    touched,
    handleSubmit,
    handleBlur,
    handleChange,
    setFieldValue,
    setValues,
  } = useFormik({
    initialValues: {
      badlaType: rowData?.badlaType ?? "",
      settingName: rowData?.settingName ?? "",
      reference: rowData?.reference ?? "",
      badla: null,
      checkBrokrage: rowData?.checkBrokrage ?? false,
      comexType: rowData?.comexType,
      comexQty: rowData?.comexQty ?? 0,
      comexPrice: rowData?.comexPrice ?? 0,
      comexBroType: brokerageTypeOptions[0],
      comexBroAmount: rowData?.comexBroAmount ?? 0,
      comexInstrument: null,
      comexDetail: null,
      comexLastBuyPrice: 0,
      comexLastSellPrice: 0,
      comexLotSize: 0,
      dgcxType: null,
      dgcxQty: rowData?.dgcxQty ?? 0,
      dgcxPrice: rowData?.dgcxPrice ?? 0,
      dgcxBroAmount: rowData?.dgcxBroAmount ?? 0,
      dgcxBroType: brokerageTypeOptions[0],
      dgcxInstrument: null,
      dgcxDetail: null,
      dgcxLastBuyPrice: 0,
      dgcxLastSellPrice: 0,
      mcxType: null,
      mcxQty: rowData?.mcxQty ?? 0,
      mcxPrice: rowData?.mcxPrice ?? 0,
      mcxBroAmount: rowData?.mcxBroAmount ?? 0,
      mcxBroType: brokerageTypeOptions[0],
      mcxInstrument: null,
      mcxDetail: null,
      mcxLastBuyPrice: 0,
      mcxLastSellPrice: 0,
      openingAmount: rowData?.openingAmount,
      duty:
        rowData?.duty || rowData?.settingValue?.[0]?.Duty || rowData?.duty || 0,
      equation: "",
      reverse: "",
      endBadlaOption: null,
      addClosingRate: false,
      dollerClosingRate: "",
      buyOrSellOption: null,
      showClosingRate: false,
      buyOrSellClosingRate: "",
      dgcxRecommendQty: 0,
    },
    enableReinitialize: true,
    validationSchema: badlaOrderSchema,
    onSubmit: (values) => {
      handleFormSubmit({ values });
    },
  });

  // submit handler
  const handleFormSubmit = useCallback(
    async ({ values }) => {
      const {
        badla,
        checkBrokrage,
        comexType,
        comexQty,
        comexPrice,
        comexBroType,
        comexBroAmount,
        comexInstrument,
        comexDetail,
        comexLastBuyPrice,
        comexLastSellPrice,
        dgcxType,
        dgcxQty,
        dgcxPrice,
        dgcxBroAmount,
        dgcxBroType,
        dgcxInstrument,
        dgcxDetail,
        dgcxLastBuyPrice,
        dgcxLastSellPrice,
        mcxType,
        mcxQty,
        mcxPrice,
        mcxBroAmount,
        mcxBroType,
        mcxInstrument,
        mcxDetail,
        mcxLastBuyPrice,
        mcxLastSellPrice,
        openingAmount,
        badlaType,
        settingName,
        reference,
        duty,
        equation,
        reverse,
      } = values;
      const rateBadlaDG = dgcxPrice ? (10000 / dgcxPrice).toFixed(2) : 0;
      const currentBadlaDG = dgcxLastBuyPrice
        ? (10000 / dgcxLastBuyPrice).toFixed(2)
        : 0;

      const payload = {
        mcxbadlaInst: mcxInstrument?.value,
        mcxType,
        mcxQty,
        mcxPrice,
        mcxBroType: mcxBroType?.value,
        mcxBroAmount,
        dgcxbadlaInst: dgcxInstrument?.value,
        dgcxType,
        dgcxQty,
        dgcxPrice,
        dgcxBroType: dgcxBroType?.value,
        dgcxBroAmount,
        comexbadlaInst: comexInstrument?.value,
        comexType,
        comexQty,
        comexPrice,
        comexBroType: comexBroType?.value,
        comexBroAmount,
        type: null,
        id: null,
        duty,
        reference,
        endandopen: null,
        openingAmount,
        mcxRolloverbadlaInst: null,
        mcxRolloverType: null,
        mcxRolloverQty: null,
        mcxRolloverPrice: null,
        mcxRolloverBroType: null,
        mcxRolloverBroAmount: null,
        comexRolloverbadlaInst: null,
        comexRolloverType: null,
        comexRolloverQty: null,
        comexRolloverPrice: null,
        comexRolloverBroType: null,
        comexRolloverBroAmount: null,
        dgcxRolloverbadlaInst: null,
        dgcxRolloverType: null,
        dgcxRolloverQty: null,
        dgcxRolloverPrice: null,
        dgcxRolloverBroType: null,
        dgcxRolloverBroAmount: null,
        reverse,
        mcxDetail: mcxInstrument,
        comexDetail: comexInstrument,
        dgcxDetail: dgcxInstrument,
        checkBrokrage,
        settingName,
        equation,
        dollerClearingRate: rateBadlaDG,
        instrumentAry: [
          {
            instrument_token: comexInstrument?.instrument_token,
            original_exchange: comexInstrument?.original_exchange,
            name: comexInstrument?.name,
            tradingsymbol: comexInstrument?.tradingsymbol,
            expiry: comexInstrument?.expiry,
          },
          {
            instrument_token: mcxInstrument?.instrument_token,
            original_exchange: mcxInstrument?.original_exchange,
            name: mcxInstrument?.name,
            tradingsymbol: mcxInstrument?.tradingsymbol,
            expiry: mcxInstrument?.expiry,
          },
          {
            instrument_token: dgcxInstrument?.instrument_token,
            original_exchange: dgcxInstrument?.original_exchange,
            name: dgcxInstrument?.name,
            tradingsymbol: dgcxInstrument?.tradingsymbol,
            expiry: dgcxInstrument?.expiry,
          },
        ],
        currentBadla: handleEquationParams({
          equation,
          l1Param: comexLastBuyPrice,
          l2Param: mcxLastBuyPrice,
          l3Param: currentBadlaDG,
          duty,
        }),
        rateBadla: handleEquationParams({
          equation,
          l1Param: comexPrice,
          l2Param: mcxPrice,
          l3Param: rateBadlaDG,
          duty,
        }),
        userId,
        isOpen: true,
        badlRefId: generateRandomId(),
      };

      const res = isEdit
        ? await putAxios({
            url: `${API_BADLA_ORDER_EDIT}/${rowData._id}`,
            payload,
          })
        : await postAxios({ url: API_BADLA_ORDER_ADD, payload });

      if (res) {
        console.log("🚀 ~ AddBadlaOrder ~ res:", res);
        if (badlaOrderRefetch) badlaOrderRefetch();
        handleShowHide();
      }
    },
    [values, userId],
  );

  const {
    comexInstrumentOptions,
    mcxInstrumentOptions,
    dgcxInstrumentOptions,
    allInstrumentOptions,
  } = useBadlaInstrumentList({ allData: values });

  // BadlarateAdd
  const handleSetInstrument = useCallback(
    (instrumentsDetail) => {
      const instrumentTokensRowdata = {
        comexInstRowData: "",
        mcxInstRowData: "",
        dgcxInstRowData: "",
      };

      // find here
      instrumentsDetail.forEach((element) => {
        if (element.exchange === "MCX")
          instrumentTokensRowdata.mcxInstRowData = element.instrument_token;
        if (element.exchange === "COMEX")
          instrumentTokensRowdata.comexInstRowData = element.instrument_token;
        if (element.exchange === "DGCX")
          instrumentTokensRowdata.dgcxInstRowData = element.instrument_token;
      });

      // set instrument
      const matchComexOption = comexInstrumentOptions.find(
        (x) => x.instrument_token === instrumentTokensRowdata.comexInstRowData,
      );

      // set mcx
      const matchMcxOption = mcxInstrumentOptions.find(
        (x) => x.instrument_token === instrumentTokensRowdata.mcxInstRowData,
      );

      // set dgcx
      const matchDgcxOption = dgcxInstrumentOptions.find(
        (x) => x.instrument_token === instrumentTokensRowdata.dgcxInstRowData,
      );

      setValues(
        (prev) => ({
          ...prev,
          comexInstrument: matchComexOption,
          mcxInstrument: matchMcxOption,
          dgcxInstrument: matchDgcxOption,
        }),
        false,
      );
    },
    [comexInstrumentOptions, mcxInstrumentOptions, dgcxInstrumentOptions],
  );

  const handleInstrument = useCallback(
    (rowData) => {
      // set instrument
      const matchComexOption = comexInstrumentOptions.find(
        (x) =>
          x.instrument_token === rowData?.comexDetail?.[0]?.instrument_token,
      );

      // set mcx
      const matchMcxOption = mcxInstrumentOptions.find(
        (x) => x.instrument_token === rowData?.mcxDetail?.[0]?.instrument_token,
      );

      // set dgcx
      const matchDgcxOption = dgcxInstrumentOptions.find(
        (x) =>
          x.instrument_token === rowData?.dgcxDetail?.[0]?.instrument_token,
      );

      setValues(
        (prev) => ({
          ...prev,
          comexInstrument: matchComexOption,
          mcxInstrument: matchMcxOption,
          dgcxInstrument: matchDgcxOption,
        }),
        false,
      );
    },
    [comexInstrumentOptions, mcxInstrumentOptions, dgcxInstrumentOptions],
  );

  // using for on load set instrument for create badla order mode
  useEffect(() => {
    if (
      !comexInstrumentOptions ||
      !mcxInstrumentOptions ||
      !dgcxInstrumentOptions
    )
      return;

    // prevent re-setting if already set
    if (values.comexInstrument || values.mcxInstrument || values.dgcxInstrument)
      return;

    if (isCreateFromBadla) handleSetInstrument(rowData.instrumentsDetail);

    if (isEdit) handleInstrument(rowData);
  }, [
    rowData,
    comexInstrumentOptions,
    mcxInstrumentOptions,
    dgcxInstrumentOptions,
    isCreateFromBadla,
  ]);

  // using for on load set instrument for edit mode
  useEffect(() => {
    if (
      !comexInstrumentOptions ||
      !mcxInstrumentOptions ||
      !dgcxInstrumentOptions
    )
      return;

    // prevent re-setting if already set
    if (values.comexInstrument || values.mcxInstrument || values.dgcxInstrument)
      return;

    handleInstrument(rowData);
  }, [
    rowData,
    comexInstrumentOptions,
    mcxInstrumentOptions,
    dgcxInstrumentOptions,
    isEdit,
  ]);

  // fetch last price of every instrument
  const fetchLastPrice = async (instrument, fieldPrefix) => {
    if (!instrument?.value) return;

    try {
      const [price] = await Promise.all([
        postFetchAxios({
          url: API_BADLA_LAST_PRICE,
          payload: [`${instrument.value}_full`],
        }),
      ]);

      if (!isEdit) {
        setFieldValue(`${fieldPrefix}LastBuyPrice`, price?.buy_price_0 ?? 0);
        setFieldValue(`${fieldPrefix}Price`, price?.buy_price_0 ?? 0);
        setFieldValue(`${fieldPrefix}LastSellPrice`, price?.sell_price_0 ?? 0);
      }
    } catch (error) {
      console.error(`Error fetching ${fieldPrefix} price:`, error);
    }
  };

  // using saperate effect for decrease rerender api calls
  useEffect(() => {
    fetchLastPrice(values?.comexInstrument, "comex");
  }, [values?.comexInstrument?.value]);

  useEffect(() => {
    fetchLastPrice(values?.mcxInstrument, "mcx");
  }, [values?.mcxInstrument?.value]);

  useEffect(() => {
    fetchLastPrice(values?.dgcxInstrument, "dgcx");
  }, [values?.dgcxInstrument?.value]);

  // uses for set type according to comextype
  useEffect(() => {
    if (!values?.comexType) return;

    const newType = values.comexType === "sell" ? "buy" : "sell";

    if (values.mcxType !== newType.value || values.dgcxType !== newType.value) {
      setFieldValue("mcxType", newType);
      setFieldValue("dgcxType", newType);
    }
  }, [values?.comexType]);

  const handleSelectBadla = useCallback(({ badlaValues }) => {
    setFieldValue("badlaType", badlaValues?.badla?.value);
    setFieldValue("settingName", badlaValues?.matchScript?.settingName);
    setFieldValue("equation", badlaValues?.matchScript?.equation);
    setFieldValue("reverse", badlaValues?.matchScript?.reverse);
  }, []);

  // using for calculate recommended DGCX qty
  useEffect(() => {
    const comexQty = isNum(values?.comexQty);
    const comexLotSize = isNum(values?.comexLotSize);
    const comexPrice = isNum(values?.comexPrice);
    const dgcxPrice = isNum(values?.dgcxPric);

    if (values?.comexLotSize && values?.comexPrice && values?.dgcxPrice) {
      const count =
        ((comexPrice * comexLotSize * (10000 / dgcxPrice)) / 2000000) *
        comexQty;

      setFieldValue("dgcxRecommendQty", count);
    }
  }, [
    values?.comexQty,
    values?.comexLotSize,
    values?.comexPrice,
    values?.dgcxPrice,
  ]);

  return (
    <>
      <form className="form-order" onSubmit={handleSubmit}>
        <div className="row  m-0 p-0 align-items-end">
          {/* reference */}
          <div className="col-lg-4 col-sm-6 col-6 m-0 p-1">
            <div className="form-group ">
              <Label className="lead_text">Reference Name</Label>
              <Input
                type="text"
                placeholder="Reference Name"
                className="form-control"
                name="reference"
                onChange={handleChange}
                onblur={handleBlur}
                value={values.reference}
                id="reference"
                autoFocus={true}
              />
              {errors?.reference && touched?.reference && (
                <Error>{errors?.reference}</Error>
              )}
            </div>
          </div>

          {/* brokerage */}
          <div className="col-lg-4 col-sm-6 col-6 m-0 p-1">
            <div className="form-group">
              <div className="custom-control p-2 w-100 d-flex justify-content-between align-items-center">
                <Label className="lead_text">Add Brokerage</Label>
                <Switch
                  name="checkBrokrage"
                  onChange={handleChange}
                  value={values.checkBrokrage}
                  getValue={values.checkBrokrage}
                  className="custom-control-input w-auto"
                />
              </div>
            </div>
          </div>

          {/* duty */}
          <div className="col-lg-4 col-sm-6 col-6 m-0 p-1">
            <div className="form-group ">
              <Label className="lead_text">Duty</Label>
              <Input
                type="number"
                placeholder="Duty"
                className="form-control"
                name="duty"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.duty}
              />
              {errors?.duty && touched?.duty && <Error>{errors?.duty}</Error>}
            </div>
          </div>

          {/* badla list */}
          <SelectBadla
            handleSelectBadla={handleSelectBadla}
            isClearForm={null}
            customClass="col-lg-4"
            badlaType={rowData?.badlaType}
            settingName={rowData?.settingName}
            isRemoveSelectedBadla={false}
            isShowMatchingBadla={true}
            // isDisabled={isEndMode}
          />

          {/* opening amount */}
          <div className="col-lg-4 col-sm-6 col-6 m-0 p-1">
            <div className="form-group ">
              <Label className="lead_text">Opening Amount(Optional)</Label>
              <Input
                type="number"
                placeholder="Opening amount"
                className="form-control"
                name="openingAmount"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.openingAmount}
              />
              {errors?.openingAmount && touched?.openingAmount && (
                <Error>{errors?.openingAmount}</Error>
              )}
            </div>
          </div>

          {/* instrument */}
          <div className="col-12 m-0 p-0">
            {/* comex */}
            <div className="row m-0 p-0">
              <div
                className={` ${
                  values?.checkBrokrage
                    ? "col-lg-2 col-sm-6 col-6 m-0 p-1"
                    : "col-lg-4 col-sm-6 col-6 m-0 p-1"
                } `}
              >
                <div className="date-input-box">
                  <Label className="lead_text">COMEX Instrument</Label>
                  <CustomSelect
                    options={comexInstrumentOptions}
                    className="dropdown-control"
                    name="comexInstrument"
                    value={values.comexInstrument}
                    onChange={(selectedOption) => {
                      setFieldValue("comexLotSize", selectedOption?.lot_size);
                      setFieldValue("comexInstrument", selectedOption);
                    }}
                    isClearable={true}
                    // isDisabled={isEndMode || isBuyMode || isSellMode}
                  />
                  {errors?.comexInstrument && touched?.comexInstrument && (
                    <Error>{errors?.comexInstrument}</Error>
                  )}
                  <p className="m-0 p-0">
                    {values?.comexLastBuyPrice ?? 456} |
                    {values?.comexLastSellPrice}
                  </p>
                </div>
              </div>
              <div
                className={` ${
                  values?.checkBrokrage
                    ? "col-lg-2 col-sm-6 col-6 m-0 p-1"
                    : "col-lg-2 col-sm-6 col-6 m-0 p-1"
                } `}
              >
                <div className="date-input-box">
                  <Label className="lead_text">Type</Label>
                  <div className="check_group">
                    <div className="form-check">
                      <Label className="lead_text" htmlFor="comexTypeBuy">
                        BUY
                      </Label>
                      <Input
                        type="radio"
                        name="comexType"
                        onChange={handleChange}
                        checked={values?.comexType === "buy"}
                        value="buy"
                        id="comexTypeBuy"
                        className="form-check-input"
                      />
                    </div>
                    <div className="form-check">
                      <Label className="lead_text" htmlFor="comexTypeSell">
                        SELL
                      </Label>
                      <Input
                        type="radio"
                        name="comexType"
                        onChange={handleChange}
                        checked={values?.comexType == "sell"}
                        value="sell"
                        id="comexTypeSell"
                        className="form-check-input"
                      />
                      {errors?.comexType && touched?.comexType && (
                        <Error>{errors?.comexType}</Error>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div
                className={` ${
                  values?.checkBrokrage
                    ? "col-lg-2 col-sm-6 col-6 m-0 p-1"
                    : "col-lg-3 col-sm-6 col-6 m-0 p-1"
                } `}
              >
                <div className="form-group ">
                  <Label className="lead_text">Qty</Label>
                  <Input
                    type="number"
                    placeholder="number"
                    className="form-control"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.comexQty}
                    id="comexQty"
                    name="comexQty"
                    // disabled={isEndMode}
                  />
                  {errors?.comexQty && touched?.comexQty && (
                    <Error>{errors?.comexQty}</Error>
                  )}
                </div>
              </div>
              <div
                className={` ${
                  values?.checkBrokrage
                    ? "col-lg-2 col-sm-6 col-6 m-0 p-1"
                    : "col-lg-3 col-sm-6 col-6 m-0 p-1"
                } `}
              >
                <div className="form-group ">
                  <Label className="lead_text">Price</Label>
                  <Input
                    type="number"
                    placeholder="Price"
                    className="form-control"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.comexPrice}
                    id="comexPrice"
                    name="comexPrice"
                  />
                  {errors?.comexPrice && touched?.comexPrice && (
                    <Error>{errors?.comexPrice}</Error>
                  )}
                </div>
              </div>
              {values?.checkBrokrage && (
                <>
                  <div className="col-lg-2 col-sm-6 col-6 m-0 p-1">
                    <div className="date-input-box">
                      <Label className="lead_text">Brokerage Type</Label>
                      <CustomSelect
                        className="dropdown-control"
                        options={brokerageTypeOptions}
                        name="comexBroType"
                        value={values.comexBroType}
                        onChange={(selectedOption) =>
                          setFieldValue("comexBroType", selectedOption)
                        }
                        isClearable={true}
                      />
                    </div>
                  </div>
                  <div className="col-lg-2 col-sm-6 col-6 m-0 p-1">
                    <div className="form-group ">
                      <Label className="lead_text">Brokerage Amount</Label>
                      <Input
                        type="number"
                        placeholder="Brokerage Amount"
                        className="form-control"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.comexBroAmount}
                        id="comexBroAmount"
                        name="comexBroAmount"
                      />
                    </div>
                  </div>
                </>
              )}
            </div>
            {/* mcx */}
            <div className="row m-0 p-0">
              <div
                className={` ${
                  values?.checkBrokrage
                    ? "col-lg-2 col-sm-6 col-6 m-0 p-1"
                    : "col-lg-4 col-sm-6 col-6 m-0 p-1"
                } `}
              >
                <div className="date-input-box">
                  <Label className="lead_text">MCX Instrument</Label>
                  <CustomSelect
                    options={mcxInstrumentOptions}
                    className="dropdown-control"
                    name="mcxInstrument"
                    value={values.mcxInstrument}
                    onChange={(selectedOption) =>
                      setFieldValue("mcxInstrument", selectedOption)
                    }
                    isClearable={true}
                    // isDisabled={isEndMode || isBuyMode || isSellMode}
                  />
                  <p className="m-0 p-0">
                    {values.mcxLastBuyPrice} | {values.mcxLastSellPrice}
                  </p>
                </div>
              </div>
              <div
                className={` ${
                  values?.checkBrokrage
                    ? "col-lg-2 col-sm-6 col-6 m-0 p-1"
                    : "col-lg-2 col-sm-6 col-6 m-0 p-1"
                } `}
              >
                <div className="date-input-box  ">
                  <Label className="lead_text">Type</Label>

                  <div className="check_group">
                    <div className="form-check">
                      <Label className="lead_text" htmlFor="mcxTypeBuy">
                        BUY
                      </Label>
                      <Input
                        type="radio"
                        name="mcxType"
                        checked={values?.mcxType === "buy"}
                        value="buy"
                        id="mcxTypeBuy"
                        disabled={true}
                        className="form-check-input"
                      />
                    </div>
                    <div className="form-check">
                      <Label className="lead_text" htmlFor="mcxTypeSell">
                        SELL
                      </Label>
                      <Input
                        type="radio"
                        name="mcxType"
                        checked={values?.mcxType == "sell"}
                        value="sell"
                        id="mcxTypeSell"
                        disabled={true}
                        className="form-check-input"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div
                className={` ${
                  values?.checkBrokrage
                    ? "col-lg-2 col-sm-6 col-6 m-0 p-1"
                    : "col-lg-3 col-sm-6 col-6 m-0 p-1"
                } `}
              >
                <div className="form-group ">
                  <Label className="lead_text">Qty</Label>
                  <Input
                    type="number"
                    placeholder="Qty"
                    className="form-control"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.mcxQty}
                    id="mcxQty"
                    name="mcxQty"
                    // disabled={isEndMode}
                  />
                  {errors?.mcxQty && touched?.mcxQty && (
                    <Error>{errors?.mcxQty}</Error>
                  )}
                </div>
              </div>
              <div
                className={` ${
                  values?.checkBrokrage
                    ? "col-lg-2 col-sm-6 col-6 m-0 p-1"
                    : "col-lg-3 col-sm-6 col-6 m-0 p-1"
                } `}
              >
                <div className="form-group ">
                  <Label className="lead_text">Price</Label>
                  <Input
                    type="number"
                    placeholder="Price"
                    className="form-control"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.mcxPrice}
                    id="mcxPrice"
                    name="mcxPrice"
                  />
                  {errors?.mcxPrice && touched?.mcxPrice && (
                    <Error>{errors?.mcxPrice}</Error>
                  )}
                </div>
              </div>
              {values?.checkBrokrage && (
                <>
                  <div className="col-lg-2 col-sm-6 col-6 m-0 p-1">
                    <div className="date-input-box">
                      <Label className="lead_text">Brokerage Type</Label>
                      <CustomSelect
                        className="dropdown-control"
                        options={brokerageTypeOptions}
                        name="mcxBroType"
                        value={values.mcxBroType}
                        onChange={(selectedOption) =>
                          setFieldValue("mcxBroType", selectedOption)
                        }
                        isClearable={true}
                      />
                    </div>
                  </div>
                  <div className="col-lg-2 col-sm-6 col-6 m-0 p-1">
                    <div className="form-group ">
                      <Label className="lead_text">Brokerage Amount</Label>
                      <Input
                        type="number"
                        placeholder="Brokerage Amount"
                        className="form-control"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.mcxBroAmount}
                        id="mcxBroAmount"
                        name="mcxBroAmount"
                      />
                    </div>
                  </div>
                </>
              )}
            </div>
            {/* dgcx */}
            <div className="row m-0 p-0">
              <div
                className={` ${
                  values?.checkBrokrage
                    ? "col-lg-2 col-sm-6 col-6 m-0 p-1"
                    : "col-lg-4 col-sm-6 col-6 m-0 p-1"
                } `}
              >
                <div className="date-input-box">
                  <Label className="lead_text">DGCX Instrument</Label>
                  <CustomSelect
                    options={dgcxInstrumentOptions}
                    className="dropdown-control"
                    name="dgcxInstrument"
                    value={values.dgcxInstrument}
                    onChange={(selectedOption) =>
                      setFieldValue("dgcxInstrument", selectedOption)
                    }
                    isClearable={true}
                    // isDisabled={isEndMode || isBuyMode || isSellMode}
                  />
                  <p className="m-0 p-0">
                    {values.dgcxLastBuyPrice} | {values.dgcxLastSellPrice}
                  </p>
                </div>
              </div>
              <div
                className={` ${
                  values?.checkBrokrage
                    ? "col-lg-2 col-sm-6 col-6 m-0 p-1"
                    : "col-lg-2 col-sm-6 col-6 m-0 p-1"
                } `}
              >
                <div className="date-input-box ">
                  <Label className="lead_text">Type</Label>
                  <div className="check_group">
                    <div className="form-check">
                      <Label className="lead_text" htmlFor="dgcxcxTypeBuy">
                        BUY
                      </Label>
                      <Input
                        type="radio"
                        name="dgcxType"
                        checked={values?.dgcxType === "buy"}
                        value="buy"
                        id="dgcxcxTypeBuy"
                        disabled={true}
                        className="form-check-input"
                      />
                    </div>
                    <div className="form-check">
                      <Label className="lead_text" htmlFor="dgcxTypeSell">
                        SELL
                      </Label>
                      <Input
                        type="radio"
                        name="dgcxType"
                        checked={values?.dgcxType == "sell"}
                        value="sell"
                        id="dgcxTypeSell"
                        disabled={true}
                        className="form-check-input"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div
                className={` ${
                  values?.checkBrokrage
                    ? "col-lg-2 col-sm-6 col-6 m-0 p-1"
                    : "col-lg-3 col-sm-6 col-6 m-0 p-1"
                } `}
              >
                <div className="form-group ">
                  <Label className="lead_text">Qty</Label>

                  <Input
                    type="number"
                    placeholder="Qty"
                    className="form-control"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.dgcxQty}
                    id="dgcxQty"
                    name="dgcxQty"
                    // disabled={isEndMode}
                  />
                  <Span className="text_type">
                    Recommend : {values?.dgcxRecommendQty ?? 0}
                  </Span>
                  {errors?.dgcxQty && touched?.dgcxQty && (
                    <Error>{errors?.dgcxQty}</Error>
                  )}
                </div>
              </div>
              <div
                className={` ${
                  values?.checkBrokrage
                    ? "col-lg-2 col-sm-6 col-6 m-0 p-1"
                    : "col-lg-3 col-sm-6 col-6 m-0 p-1"
                } `}
              >
                <div className="form-group ">
                  <Label className="lead_text">Price</Label>
                  <Input
                    type="number"
                    placeholder="Price"
                    className="form-control"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.dgcxPrice}
                    id="dgcxPrice"
                    name="dgcxPrice"
                  />
                  {errors?.dgcxPrice && touched?.dgcxPrice && (
                    <Error>{errors?.dgcxPrice}</Error>
                  )}
                </div>
              </div>
              {values?.checkBrokrage && (
                <>
                  <div className="col-lg-2 col-sm-6 col-6 m-0 p-1">
                    <div className="date-input-box">
                      <Label className="lead_text">Brokerage Type</Label>
                      <CustomSelect
                        className="dropdown-control"
                        options={brokerageTypeOptions}
                        name="dgcxBroType"
                        value={values.dgcxBroType}
                        onChange={(selectedOption) =>
                          setFieldValue("dgcxBroType", selectedOption)
                        }
                        isClearable={true}
                      />
                    </div>
                  </div>
                  <div className="col-lg-2 col-sm-6 col-6 m-0 p-1">
                    <div className="form-group ">
                      <Label className="lead_text">Brokerage Amount</Label>
                      <Input
                        type="number"
                        placeholder="Brokerage Amount"
                        className="form-control"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.dgcxBroAmount}
                        id="dgcxBroAmount"
                        name="dgcxBroAmount"
                      />
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        <Button type="submit" id="form-submit-btn" hidden={true}></Button>
      </form>
    </>
  );
};

export default AddBadlaOrder;
